import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hiddenInput", "button", "menu", "placeholder", "input", "results"]
  static values = { isCountryField: Boolean }

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)
    document.addEventListener('keydown', this.boundHandleKeydown)
    this.buttonTarget.setAttribute('aria-expanded', 'false')
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }

  toggle() {
    if (this.menuTarget.classList.contains('hidden')) {
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    this.menuTarget.classList.remove('hidden')
    this.buttonTarget.setAttribute('aria-expanded', 'true')
    this.positionMenu()
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
      this.showAllItems()
    }
  }

  positionMenu() {
    // Only position if menu uses fixed positioning
    if (getComputedStyle(this.menuTarget).position !== 'fixed') return

    const button = this.buttonTarget
    const rect = button.getBoundingClientRect()

    this.menuTarget.style.top = `${rect.bottom + 4}px`
    this.menuTarget.style.left = `${rect.left}px`
    this.menuTarget.style.width = `${rect.width}px`
  }

  hide() {
    this.menuTarget.classList.add('hidden')
    this.buttonTarget.setAttribute('aria-expanded', 'false')
  }

  select(event) {
    const value = event.currentTarget.dataset.value
    const text = event.currentTarget.textContent.trim()

    this.hiddenInputTarget.value = value
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.placeholderTarget.textContent = text
    this.placeholderTarget.classList.remove('text-gray-500')
    this.placeholderTarget.classList.add('text-black', 'font-medium')
    
    // Dispatch country change event if this is a country dropdown
    if (this.isCountryFieldValue) {
      const event = new CustomEvent('country-changed', {
        detail: { countryId: value, countryName: text, country: value },
        bubbles: true
      })
      document.dispatchEvent(event)
      
      // Also dispatch billing country specific event for VAT section
      if (this.hiddenInputTarget.name === 'tenant[billing_country]') {
        const vatContainer = document.querySelector('[data-controller~="vat-input"]')
        if (vatContainer) {
          const vatEvent = new CustomEvent('countryChanged', {
            detail: { country: value },
            bubbles: true
          })
          vatContainer.dispatchEvent(vatEvent)
        }
      }
    }
    
    this.hide()
  }

  search(event) {
    const query = event.target.value.toLowerCase()
    const items = this.resultsTarget.querySelectorAll('button')
    
    items.forEach(item => {
      const name = item.dataset.name
      if (name && name.includes(query)) {
        item.classList.remove('hidden')
      } else {
        item.classList.add('hidden')
      }
    })
  }

  handleKeydown(event) {
    if (this.menuTarget.classList.contains('hidden')) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.hide()
      this.buttonTarget.focus()
      return
    }

    if (!this.hasResultsTarget) return

    const items = Array.from(this.resultsTarget.querySelectorAll('button:not(.hidden)'))
    if (items.length === 0) return

    const currentIndex = items.indexOf(document.activeElement)
    
    switch(event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1].focus()
        } else {
          items[0].focus()
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (currentIndex > 0) {
          items[currentIndex - 1].focus()
        } else {
          items[items.length - 1].focus()
        }
        break
      case 'Enter':
        if (document.activeElement.tagName === 'BUTTON') {
          event.preventDefault()
          document.activeElement.click()
        }
        break
      default:
        // Only do letter-jump if we're not focused on the search input
        if (!this.hasInputTarget || !this.inputTarget.matches(':focus')) {
          if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
            const letter = event.key.toLowerCase()
            const firstMatch = items.find(item => 
              item.dataset.name && item.dataset.name.toLowerCase().startsWith(letter)
            )
            if (firstMatch) {
              firstMatch.focus()
            }
          }
        }
    }
  }

  showAllItems() {
    const items = this.resultsTarget.querySelectorAll('button')
    items.forEach(item => item.classList.remove('hidden'))
  }
}
